/**
 * Token Manager Module
 *
 * Handles authentication token management including:
 * - Token retrieval and storage
 * - JWT decoding and validation
 * - Header injection for authenticated requests
 * - QR sign-in flow
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { decode as atob } from 'base-64';
import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { t } from '../../i18n';

/**
 * Decode a JWT token to extract its payload
 * @param {string} token - The JWT token
 * @returns {Object|null} The decoded payload or null if invalid
 */
export function decodeJWT(token) {
  try {
    const [, payload] = token.split('.');
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (err) {
    return null;
  }
}

/**
 * Get the current user's email from Cognito or token
 * @returns {Promise<string|undefined>} The user's email
 */
export async function getUserEmail() {
  try {
    const user = await fetchUserAttributes();
    return user.email;
  } catch (e) {
    const decoded = decodeJWT(await getToken());
    return decoded?.email;
  }
}

/**
 * Add authorization headers to request options
 * @param {Object} options - Request options object
 * @returns {Promise<Object>} Options with headers added
 */
export async function addHeaders(options) {
  if (options && options['headers']) {
    return options;
  }

  const token = await getToken();

  options['headers'] = {
    Authorization: token,
  };
  return options;
}

/**
 * Check if the current token is still valid
 * @returns {Promise<boolean>} True if token is valid
 */
export async function isTokenValid() {
  const token = await getToken();
  if (!token) {
    return false;
  }

  const decoded = decodeJWT(token);

  if (decoded === null) {
    return false;
  }

  const exp = decoded?.exp ? decoded.exp * 1000 : 0;

  return exp > Date.now();
}

/**
 * Get the current authentication token
 * Tries Amplify session first, falls back to AsyncStorage
 * @returns {Promise<string|null>} The auth token
 */
export async function getToken() {
  try {
    const user = await fetchAuthSession();
    return user.tokens.idToken.toString();
  } catch (e) {
    const token = await AsyncStorage.getItem('token');
    return token;
  }
}

/**
 * Store an authentication token
 * @param {string} token - The token to store
 */
export async function storeToken(token) {
  await AsyncStorage.setItem('token', token);
}

/**
 * Execute QR code sign-in flow
 * @param {string} organization - The organization to sign into
 * @param {boolean} noLogin - Whether to skip normal login
 * @param {Function} apiGet - The API get function (passed to avoid circular deps)
 * @param {Function} setOrganizationSettings - Function to set org settings
 */
export async function executeQRSignIn(organization, noLogin, apiGet, setOrganizationSettings) {
  try {
    // Always clear organizationAccess when using QR sign-in
    // QR users have different permissions than regular Cognito users
    await AsyncStorage.removeItem('organizationAccess');

    if (!(await isTokenValid())) {
      const response = await apiGet('qrApi', '/qr-auth', {}, noLogin);
      const token = response['idToken'];
      await storeToken(token);
      await AsyncStorage.setItem('method', 'qr');
    }

    const current_org = await AsyncStorage.getItem('organization');
    const login = await AsyncStorage.getItem('method');

    if ((current_org && current_org != organization) || !current_org || !login || login !== 'qr') {
      await setOrganizationSettings(organization);
    }
  } catch (error) {
    let message = error.toString();
    if (!message.includes('Pending sign-in')) {
      // Use alert instead of toast to make sure the user sees it.
      alert(t('errors:auth.loginFailed'), error.message);
    }
  }
}

/**
 * Set organization settings in AsyncStorage
 * @param {string} organization - The organization name
 * @param {Function} apiGet - The API get function
 */
export async function setOrganizationSettings(organization, apiGet) {
  await AsyncStorage.removeItem('routing');
  await AsyncStorage.removeItem('organization');

  const request = { queryParams: { organization: organization } };
  const organizationData = await apiGet('Resty', '/items/organization', request);

  const routing = organizationData['data']['routing'] ? organizationData['data']['routing'] : [];

  await AsyncStorage.setItem('routing', JSON.stringify(routing));
  await AsyncStorage.setItem('organization', organization);
}
